import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { ArrowLeft, Layers, Shield, Sparkles, SlidersHorizontal } from 'lucide-react';
import { BRAND_NAME } from '../brand';

const LearnMorePage: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 px-4 py-12">
            {/* Background orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-32 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
            </div>

            <div data-testid="learn-more-page" className="max-w-4xl mx-auto relative z-10">
                <button
                    data-testid="learn-more-back"
                    onClick={() => navigate('/')}
                    className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-teal-300 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                </button>

                <div className="mt-6 flex items-center gap-3">
                    <Logo variant="wordmark" size={24} appearance="dark" />
                </div>

                <div className="mt-6 space-y-4">
                    <h1 className="text-3xl sm:text-4xl font-bold leading-tight">
                        Multi-domain triage, designed for real life.
                    </h1>
                    <p className="text-slate-400 text-base sm:text-lg max-w-2xl leading-relaxed">
                        {BRAND_NAME} helps you turn noisy email into actionable work items. Pick a mode (or create your own),
                        connect Gmail when you’re ready, and keep everything organized in one place.
                    </p>
                </div>

                <div className="mt-10 grid gap-4 sm:grid-cols-2">
                    <Card
                        icon={<Layers className="w-4 h-4" />}
                        title="Modes (Domains)"
                        desc="Job Search and Family are built in. Create a custom mode during onboarding if you want something different."
                    />
                    <Card
                        icon={<SlidersHorizontal className="w-4 h-4" />}
                        title="Personalization"
                        desc="Tell us your name and timezone so dates and times make sense for you."
                    />
                    <Card
                        icon={<Sparkles className="w-4 h-4" />}
                        title="AI Assist"
                        desc={`${BRAND_NAME} can summarize a bill or note and suggest a next step. You decide. It is not a chat bot.`}
                    />
                    <Card
                        icon={<Shield className="w-4 h-4" />}
                        title="Security First"
                        desc="Sign-in and Gmail connect are separate steps. You control what’s connected and can revoke at any time."
                    />
                </div>

                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                    <button
                        data-testid="learn-more-get-started"
                        onClick={() => navigate('/login')}
                        className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-teal-500 to-teal-400 text-slate-900 font-semibold shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:scale-[1.02] transition-all duration-200"
                    >
                        Get Started
                    </button>
                    <button
                        data-testid="learn-more-welcome"
                        onClick={() => navigate('/')}
                        className="px-6 py-3.5 rounded-xl border border-white/10 text-slate-100 hover:bg-white/5 hover:border-white/20 transition-all duration-200"
                    >
                        Back to Welcome
                    </button>
                </div>
            </div>
        </div>
    );
};

const Card = ({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) => (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-5 py-5 hover:bg-white/10 hover:border-white/20 transition-all duration-200">
        <div className="flex items-center gap-2 mb-2">
            <span className="text-teal-400">{icon}</span>
            <span className="text-sm font-semibold text-slate-100">{title}</span>
        </div>
        <div className="text-xs text-slate-400 leading-relaxed">{desc}</div>
    </div>
);

export default LearnMorePage;

